import { Location } from '@angular/common';
import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  Injector,
  NgZone,
  OnDestroy,
  signal,
  Type,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { LassiApplication, SelectedSurveyMarkDto } from '@core/api/angular';
import { APPLICATION } from '@lassi/config';
import L, {
  Control,
  divIcon,
  GeoJSON,
  latLng,
  LatLng,
  latLngBounds,
  LatLngTuple,
  Layer,
  LayerGroup,
  layerGroup,
  Map as LeafletMap,
  MapOptions,
  marker,
  TileLayer,
} from 'leaflet';
import 'proj4leaflet';
import { Proj4GeoJSONFeature } from 'proj4leaflet';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  pairwise,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { GeoserverCRS } from '../feature.model';
import { LayerManagementService } from '../layer-management.service';
import { isLayerInactive, MapLayer } from '../layers';
import { MarkupLayer } from '../markup.model';
import { isIOS } from '../platform';
import { VicGridCRS } from '../projections';
import { SearchResultService } from '../search-result.service';
import { ToastService } from '../toast/toast.service';
import { parseCoordinateString } from './coordinate.utils';
import { SelectMode } from './map-mode';
import { PanMode } from './map-mode/pan.mode';
import { LassiMap, MapMode, MapModeOptions, MapModes } from './map-mode/shared';
import {
  BASE_LAYERS,
  createMarker,
  createOverlayLayer,
  DEFAULT_MAP_ZOOM_LEVEL,
  FALLBACK_ZOOM_LEVEL,
  mapAttribution,
  metro,
  victoria,
} from './map.model';

interface MapModeRef<T extends object | undefined> {
  injector: Injector;
  instance: MapMode<T>;
}

@Injectable({
  providedIn: 'root',
})
export class MapService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);

  private readonly curMapMode = signal<MapModeRef<any> | null>(null);
  resetMapMode() {
    return this.switchMapMode(PanMode);
  }

  switchMapMode<T extends MapMode<U>, U extends object | undefined>(
    ...args: T['options'] extends undefined
      ? [MapModeType: Type<T>]
      : [MapModeType: Type<T>, options: T['options']]
  ): T {
    const [MapModeType, options] = args;

    const injector = Injector.create({
      providers: [
        MapModeType,
        { provide: MapModeOptions, useValue: options },
        { provide: NgZone, useValue: this.zone },
        { provide: LassiMap, useValue: this.map },
      ],
    });
    if (this.curMapMode()) {
      (this.curMapMode()?.injector as any).destroy();
    }
    const mode = injector.get(MapModeType);
    mode.setup();
    this.curMapMode.set({
      injector: injector,
      instance: mode,
    });

    return mode;
  }

  readonly mapMode = computed(
    () => this.curMapMode()?.instance?.type ?? MapModes.PAN,
  );
  readonly mapModeTooltip = computed(() => {
    const mode = this.curMapMode()?.instance;
    if (mode instanceof SelectMode) {
      return mode.options.stopTooltip;
    }
    return undefined;
  });

  private readonly layerManagementService = inject(LayerManagementService);
  private readonly baseLayers = inject(BASE_LAYERS);
  private readonly toastService = inject(ToastService);

  private readonly baseLayer$ = this.layerManagementService.urlBaseLayer$;

  public baseLayer: TileLayer.WMS | null = null;
  readonly layerGroup = layerGroup();
  readonly markupLayerGroup: L.LayerGroup<MarkupLayer> =
    layerGroup<MarkupLayer>();

  private readonly searchResultsLayer = layerGroup<GeoJSON>();
  readonly selectedSurveyMarksLayer: LayerGroup<GeoJSON> =
    layerGroup<GeoJSON>();

  private readonly DOC_STATUS_LAYER_SCALE_THRESHOLD = 50_000;
  readonly docStatusLayer: LayerGroup<GeoJSON> = layerGroup<GeoJSON>();

  readonly spearParcelSelectedLayer: LayerGroup<GeoJSON> =
    layerGroup<GeoJSON>();

  private readonly isIos = isIOS();

  private readonly searchResultService = inject(SearchResultService);

  public readonly mapMoveEnd$ = new Subject<void>();
  private readonly onMoveEnd = () => this.mapMoveEnd$.next();

  constructor() {
    const app = inject(APPLICATION);

    if (app === LassiApplication.SPEAR) {
      effect(() => {
        const map = this.mapSignal();
        if (!map) return;

        this.spearParcelSelectedLayer.addTo(map);
      });
    }

    this.destroyRef.onDestroy(() => this.stopWatchingLocation());

    this.scaleFactor$
      .pipe(
        startWith(0),
        pairwise(),
        switchMap(([prev, cur]) =>
          this.layerManagementService.autoSwitchLayers(prev, cur),
        ),

        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    effect(() => {
      const map = this.mapSignal();
      if (!map) return;

      const scale = this.scaleFactor();
      if (scale && scale > this.DOC_STATUS_LAYER_SCALE_THRESHOLD) {
        this.docStatusLayer.removeFrom(map);
      } else {
        this.docStatusLayer.addTo(map);
      }
    });

    effect(() => {
      const map = this._map();

      if (!map) return;

      const results = new Set(
        Object.values(this.searchResultService.searchResults()),
      );

      this.searchResultsLayer.eachLayer((layer) => {
        if (!results.has(layer)) {
          this.searchResultsLayer.removeLayer(layer);
        }
      });

      results.forEach((feature) => {
        this.searchResultsLayer.addLayer(feature);
      });

      const bounds = L.geoJson(this.searchResultsLayer.toGeoJSON()).getBounds();
      if (bounds.isValid()) {
        this.map.fitBounds(bounds.pad(0.1), {
          maxZoom: DEFAULT_MAP_ZOOM_LEVEL,
        });
      }
    });
  }

  private readonly _map = signal<LeafletMap | null>(null);
  public get map(): LeafletMap {
    const map = this._map();
    if (!map) {
      throw new Error('Map not ready');
    }
    return map;
  }

  public mapSignal = computed(() => this._map());

  public resizeObserver = new ResizeObserver(() => {
    this.map.invalidateSize();
  });

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly urlZoom$ = this.activatedRoute.queryParamMap.pipe(
    map((paramMap) => paramMap.get('z')),
    distinctUntilChanged(),
    map((z) => (z ? parseFloat(z) : null)),
    shareReplay(1),
  );
  private readonly center$ = this.activatedRoute.queryParamMap.pipe(
    map((paramMap) => paramMap.get('c') || ''),
    distinctUntilChanged(),
    map((centerStr) => {
      let center: LatLngTuple | undefined;
      const latLngStr = centerStr?.split(',');
      const lat = parseFloat(latLngStr?.[0]);
      const lng = parseFloat(latLngStr?.[1]);
      if (!isNaN(lat) && !isNaN(lng)) {
        center = [lat, lng];
      }
      return center ? latLng(center) : null;
    }),
    shareReplay(1),
  );

  public mousePos$ = new BehaviorSubject<LatLng | null>(null);
  private readonly leafletCenter$ = new BehaviorSubject<LatLng | null>(null);
  public currentLatLng = toSignal(
    combineLatest([
      this.leafletCenter$.pipe(filter(Boolean)),
      this.mousePos$,
    ]).pipe(map(([center, mousePos]) => mousePos ?? center)),
  );

  public mapExtent = toSignal(
    this.leafletCenter$.pipe(
      debounceTime(500),
      map((center) => {
        if (!center) return null;

        return this.map.getBounds();
      }),
    ),
  );

  private readonly leafletZoom$ = new BehaviorSubject<number | null>(null);

  public scaleFactor$ = this.leafletZoom$.pipe(
    filter(Boolean),
    map(() => {
      try {
        return this.getCurrentScaleFactor();
      } catch (_e: unknown) {
        return NaN;
      }
    }),
    filter((value) => !isNaN(value)),
  );

  private readonly scaleFactor = toSignal(this.scaleFactor$);

  public addControl(control: Control) {
    control.addTo(this.map);
  }

  public options: MapOptions = {
    layers: [
      this.searchResultsLayer,
      this.selectedSurveyMarksLayer,
      this.docStatusLayer,
      this.markupLayerGroup,
    ],
    crs: VicGridCRS,
    maxZoom: 15,
    zoomSnap: 0.0001,
    zoomDelta: 0.5,
    maxBounds: victoria.pad(0.1),
    zoomControl: false,
    // We don't want the map to zoom beyond min zoom and then bounce back when
    // pinch-zooming, to prevent the scale-jump issue
    // https://leafletjs.com/reference.html#map-option
    bounceAtZoomLimits: false,
    // attributionControl: false,
  };

  private setBaseLayer(name: string) {
    let newBaseLayer: TileLayer.WMS | null = null;
    for (const [layerName, layer] of Object.entries(this.baseLayers)) {
      if (!layer) {
        continue;
      }
      if (layerName === name) {
        newBaseLayer = layer;
        this.map.addLayer(layer);
        layer.bringToBack();
      } else {
        this.map.removeLayer(layer);
      }
    }

    this.baseLayer = newBaseLayer;
  }

  public initMap(
    leafletMap: LeafletMap,
    options?: {
      initCenter?: LatLng;
      initZoom?: number;
    },
  ) {
    const { initCenter, initZoom } = options ?? {};
    this._map.set(leafletMap);
    this.addScaleControl();
    this.resetMapMode();

    this.map.attributionControl
      .addAttribution(mapAttribution)
      .setPrefix('')
      .setPosition('bottomleft');
    if (initCenter) {
      this.map.panTo(initCenter, {
        noMoveStart: true,
      });

      this.map.setZoom(initZoom ?? DEFAULT_MAP_ZOOM_LEVEL, {
        noMoveStart: true,
      });
    } else {
      this.map.fitBounds(victoria, {
        noMoveStart: true,
      });
    }

    this.layerGroup.addTo(this.map);
    this.synchronizeView();

    combineLatest([
      this.layerManagementService.activeMapLayers$,
      this.scaleFactor$,
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([activeLayers, scale]) => {
        this.synchroniseLayers(activeLayers, scale);
      });

    this.baseLayer$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((baseLayer) => {
        this.setBaseLayer(baseLayer);
      });

    this.map.on('zoom', (e) => this.leafletZoom$.next(e.target.getZoom()));
    this.map.on('move', (e) => this.leafletCenter$.next(e.target.getCenter()));
    this.leafletZoom$.next(this.map.getZoom());
    this.leafletCenter$.next(this.map.getCenter());

    this.map.on('mousemove', (event: L.LeafletMouseEvent) => {
      this.mousePos$.next(event.latlng);
    });
    this.map.on('mouseout', () => {
      this.mousePos$.next(null);
    });
    this.map.on('moveend', this.onMoveEnd);

    this.resizeObserver.observe(this.map.getContainer());
  }

  ngOnDestroy(): void {
    const map = this._map();
    if (map) {
      this.map.off('moveend', this.onMoveEnd);
    }
    this.mapMoveEnd$.complete();
  }

  private synchronizeView() {
    const urlState$ = merge(this.center$, this.urlZoom$).pipe(
      debounceTime(200),
      withLatestFrom(this.center$, this.urlZoom$),
      map(([, center, zoom]) => ({ center, zoom })),
    );

    const leafletCenter$ = fromEvent(this.map, 'movestart').pipe(
      switchMap(() => fromEvent(this.map, 'moveend').pipe(take(1))),
    );
    const leafletZoom$ = fromEvent(this.map, 'movestart').pipe(
      switchMap(() =>
        merge(
          fromEvent(this.map, 'zoomend'),
          fromEvent(this.map, 'moveend'),
        ).pipe(take(1)),
      ),
    );

    const leafletState$ = merge(leafletCenter$, leafletZoom$).pipe(
      debounceTime(200),
      map(() => ({
        center: this.map.getCenter(),
        zoom: this.map.getZoom(),
      })),
    );

    leafletState$.subscribe(({ center, zoom }) => {
      const urlTree = this.router.createUrlTree([], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
        queryParams: {
          c: `${center.lat},${center.lng}`,
          z: zoom,
        },
      });
      // Keep map c/z query params in sync without cancelling in-flight route navigations.
      this.location.replaceState(this.router.serializeUrl(urlTree));
    });

    const routerBackOrForward$ = this.router.events.pipe(
      filter((e) => e instanceof NavigationStart),
      map((e) => e.navigationTrigger),
      filter((trigger) => trigger === 'popstate'),
    );

    routerBackOrForward$
      .pipe(switchMap(() => urlState$.pipe(take(1))))
      .subscribe(({ center, zoom }) => {
        if (center && zoom) {
          this.map.setZoom(zoom, {
            noMoveStart: true,
          });
          this.map.panTo(center, {
            noMoveStart: true,
          });
        } else {
          this.map.fitBounds(victoria);
        }
      });
  }

  private layersMap: Record<string, TileLayer.WMS> = {};
  synchroniseLayers(
    activeLayers: (MapLayer & { status: boolean; opacity: number })[],
    scale: number,
  ) {
    let i = 0;
    const activeLayersSet = new Set<Layer>();
    const maxZIndex = Object.keys(activeLayers).length + 1;

    for (const layer of activeLayers) {
      i++;

      const zIndex = maxZIndex - i;
      this.layersMap[layer.name] =
        this.layersMap[layer.name] ||
        createOverlayLayer(layer.name, layer.useTiledWMS, this.isIos, zIndex);
      const leafletLayer = this.layersMap[layer.name];

      leafletLayer.setOpacity(layer.opacity);
      leafletLayer.setZIndex(zIndex);

      if (!layer.status || isLayerInactive(layer, scale)) {
        continue;
      }

      this.layerGroup.addLayer(leafletLayer);
      activeLayersSet.add(leafletLayer);
    }

    this.layerGroup.eachLayer((layer) => {
      if (activeLayersSet.has(layer)) {
        return;
      }
      this.layerGroup.removeLayer(layer);
    });
  }

  goTo(center: { lat: number; lng: number }, zoom?: number) {
    this.map.setView(
      latLng(center.lat, center.lng),
      zoom ?? this.map.getZoom(),
    );
  }

  goToFeatureOrRoad(
    geojson?: GeoJSON.LineString | GeoJSON.Point,
    crs?: GeoserverCRS,
  ) {
    const tempLayer = L.Proj.geoJson({ ...geojson, crs } as never);
    const bounds = tempLayer.getBounds();
    this.map.fitBounds(bounds.pad(0.1), {
      maxZoom: FALLBACK_ZOOM_LEVEL,
    });
  }

  goToMetro() {
    this.map.fitBounds(metro);
  }

  goToVictoria() {
    this.map.fitBounds(victoria);
  }

  private readonly currentLocationMarker = marker([0, 0], {
    interactive: false,
    icon: divIcon({
      className: '',
      html: '<div class="gps-dot"><div class="gps-dot-inner"></div></div>',
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    }),
  });

  private locationWatchId: number | null = null;

  goToCurrentLocation() {
    if (!('geolocation' in navigator)) {
      this.toastService.showErrorMessage(
        'Geolocation not supported by this browser',
        3000,
      );
      console.error('Geolocation not supported by this browser.');
      return;
    }

    const updateMarker = (position: GeolocationPosition) => {
      const latlng: [number, number] = [
        position.coords.latitude,
        position.coords.longitude,
      ];
      this.currentLocationMarker.setLatLng(latlng).addTo(this.map);
    };

    const handleLocationError = (error: GeolocationPositionError) => {
      this.toastService.showErrorMessage('Could not get your location', 3000);
      console.error('Error fetching current location:', error);
      this.stopWatchingLocation();
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        updateMarker(position);
        const latlng: [number, number] = [
          position.coords.latitude,
          position.coords.longitude,
        ];
        // Only pan/zoom the map when getToCurrentLocation is called, but
        // the watchPosition will not pan/zoom.
        this.map.setView(latLng(...latlng), 11);
      },
      handleLocationError,
      { maximumAge: 5000, timeout: 10_000 },
    );
    this.locationWatchId ??= navigator.geolocation.watchPosition(
      updateMarker,
      (error: GeolocationPositionError) => {
        if (error.code === error.PERMISSION_DENIED) {
          handleLocationError(error);
        } else {
          // Transient: sensor switch, brief signal loss, etc. — keep tracking
          console.warn(
            'Geolocation temporarily unavailable, retrying...',
            error,
          );
        }
      },
      { maximumAge: 5000 },
    );
  }

  stopWatchingLocation() {
    if (this.locationWatchId !== null) {
      navigator.geolocation?.clearWatch(this.locationWatchId);
      this.locationWatchId = null;
    }
    this.currentLocationMarker.remove();
  }

  public googleMapsUrl = toSignal(
    combineLatest([
      this.leafletZoom$.pipe(filter((z): z is number => z !== null)),
      this.leafletCenter$.pipe(filter((c): c is LatLng => c !== null)),
    ]).pipe(
      map(
        ([zoom, coord]) =>
          `https://www.google.com/maps/@${coord.lat},${coord.lng},${zoom + 7}z`,
      ),
    ),
  );

  private addScaleControl() {
    const scaleControl = L.control.scale({
      maxWidth: 200,
      metric: true,
      imperial: false,
      position: 'bottomleft',
    });

    this.map.addControl(scaleControl);
  }

  /**
   * Algorithm to calculate the scale factor
   * Reference: https://github.com/MarcChasse/leaflet.ScaleFactor
   * The calculation to determine the scale factor is approximate and could likely be improved further, as of right now its calculated as follows.
   *
   * Find half the map height in pixels (middle of map)
   * Get Leaflet to determine the lat/long of (0,middle of map)
   * Get Leaflet to determine the lat/long of (100,middle of map)
   * Get Leaflet to calculate the real world distance in meters between the two points.
   * Get the pixels per physical screen millimeter
   * Multiply 100px by the pixels/mm
   * Convert physical screen size of 100px from milimeters to meters
   * Divide real world meters per 100px by screen meters per 100px
   * Format and display the scale
   * */
  public getCurrentScaleFactor(): number {
    const mapInstance = this.map;

    const DEFAULT_DPI = 96;
    const MM_PER_INCH = 25.4;
    const PIXELS_PER_METER = 1000;

    const mapHeight = mapInstance.getSize().y;
    const middleLatLng = mapInstance.containerPointToLatLng([0, mapHeight / 2]);
    const leftPoint = mapInstance.latLngToContainerPoint(middleLatLng);
    const rightPoint = [leftPoint.x + 100, leftPoint.y];
    const leftLatLng = mapInstance.containerPointToLatLng(leftPoint);
    const rightLatLng = mapInstance.containerPointToLatLng(
      rightPoint as [number, number],
    );
    const distance = leftLatLng.distanceTo(rightLatLng);

    const pixelsPerMm = DEFAULT_DPI / MM_PER_INCH;
    const screenDistance = 100 / pixelsPerMm / PIXELS_PER_METER;

    return Math.round(distance / screenDistance);
  }

  public goToScale(scale: number, padding: boolean) {
    const res = this.scaleToResolution(scale);
    const mapSize = this.map.getSize();

    const halfWDeg = (mapSize.x * res) / 2;
    const halfHDeg = (mapSize.y * res) / 2;

    const center = this.map.getCenter();

    const extent = latLngBounds(
      [center.lat - halfHDeg, center.lng - halfWDeg],
      [center.lat + halfHDeg, center.lng + halfWDeg],
    );

    this.map.fitBounds(extent, {
      padding: padding ? [16, 16] : [-16, -16],
    });
  }

  private scaleToResolution(scale: number) {
    const normalisedScale = scale > 1 ? 1 / scale : scale;

    const DEFAULT_DPI = 96;
    const dpi = DEFAULT_DPI * window.devicePixelRatio;

    const INCHES_PER_UNIT = 4374754;

    return 1 / (normalisedScale * INCHES_PER_UNIT * dpi);
  }

  public highlight(polygon: GeoJSON.GeometryObject, crs?: GeoserverCRS) {
    return this.createHighlightGeometry(polygon, crs).addTo(this.map);
  }

  public createHighlightGeometry(
    polygon: GeoJSON.GeometryObject,
    crs?: GeoserverCRS,
  ) {
    // the proj4leaflet types are not good...
    return L.Proj.geoJson({ ...polygon, crs } as never, {
      style: {
        className: 'pointer-events-none',
        fillRule: 'nonzero',
      },
      pointToLayer: (_: never, latlng: LatLng) => createMarker(latlng),
    });
  }

  public reloadOverlays() {
    this.layerGroup.eachLayer((layer) => {
      // https://gis.stackexchange.com/a/270454
      // workaround to bust the leaflet/browser cache
      (layer as any).setParams({
        cache: Date.now().toString(),
      });
    });
  }

  /**
   * Parse latlng string to LatLng object.
   * Latitude and longitude values in degrees, minutes, seconds have the format of ddd mm ss.ssss.
   * For example, -38° 14' 16.2532" should be entered as -38 14 16.2532 as an argument.
   * Also, the function accepts decimal degrees format of ddd.dddddd. For example, 38.237570333
   *
   * Since in Australia, we accept lat as positive input, and lng as negative input,
   * but convert them to the opposite values for Leaflet.
   *
   * @param lat Latitude string in DMS or decimal format
   * @param lng Longitude string in DMS or decimal format
   * @returns Leaflet LatLng object or null if parsing fails
   */
  public parseLatLng(lat: string, lng: string): LatLng | null {
    try {
      let parsedLat = parseCoordinateString(lat);
      let parsedLng = parseCoordinateString(lng);

      if (parsedLat === null || parsedLng === null) {
        return null;
      }

      // Convert lat and lng per Australian convention
      parsedLat = -Math.abs(parsedLat);
      parsedLng = Math.abs(parsedLng);

      // Validate latitude range
      if (parsedLat < -90) {
        console.error(
          'Invalid latitude value. Must be between -90 and 90 degrees.',
        );
        return null;
      }

      // Validate longitude range (-180 to 180)
      if (parsedLng > 180) {
        console.error(
          'Invalid longitude value. Must be between -180 and 180 degrees.',
        );
        return null;
      }

      return new LatLng(parsedLat, parsedLng);
    } catch (error) {
      console.error('Error parsing coordinates:', error);
      return null;
    }
  }

  public goToSelectedMarks(
    surveyMarks: Pick<SelectedSurveyMarkDto, 'geometry'>[],
  ) {
    const bounds = latLngBounds([]);

    surveyMarks.forEach((mark) => {
      const markBounds = L.Proj.geoJson(
        mark.geometry as Proj4GeoJSONFeature,
      ).getBounds();

      if (markBounds.isValid()) {
        bounds.extend(markBounds);
      }
    });

    if (bounds.isValid()) {
      this.map.fitBounds(bounds.pad(0.1), {
        maxZoom: FALLBACK_ZOOM_LEVEL,
      });
    }
  }
}
